import Menu from '@mui/material/Menu'
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import {
  Checkbox,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Radio,
  TextField,
  Typography,
} from '@mui/material'
import { SearchMinor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { ActionButton } from 'src/components/shared/ActionButton'

interface TableSelectOption<V> {
  value: V
  label?: string
  component: React.ReactNode
}

interface Props<V extends string> {
  title?: string
  icon?: any
  options: TableSelectOption<V>[]
  selectedValues: Array<V>
  emptyText?: string
  onEmpty?(): void
  onSelect(values: Array<V>): void
  searchable?: boolean
  /**
   * One choice at a time, rendered with radios. For filters where combining options is
   * meaningless — picking a wider window already covers every narrower one, so a multi-select
   * would only add checkmarks that change nothing.
   */
  singleSelect?: boolean
  searchPlaceholder?: string
  menuWidth?: number
  /** Menu trigger (ActionButton) for QA */
  triggerDataTestId?: string
  clearAllDataTestId?: string
  manageOptionsDataTestId?: string
  searchInputDataTestId?: string
}

const TableSelectComponent = <V extends string>({
  title = 'Select',
  icon,
  options,
  selectedValues,
  emptyText,
  onSelect,
  onEmpty,
  searchable,
  singleSelect,
  searchPlaceholder = 'Search',
  menuWidth = 296,
  triggerDataTestId,
  clearAllDataTestId,
  manageOptionsDataTestId,
  searchInputDataTestId,
}: Props<V>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return options
    }

    const lowerQuery = searchQuery.toLowerCase()

    return options.filter((option) => (option.label ?? '').toLowerCase().includes(lowerQuery))
  }, [options, searchable, searchQuery])

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
    setSearchQuery('')
  }, [])

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }, [])

  const handleToggle = useCallback(
    (value: V) => {
      const currentIndex = selectedValues.indexOf(value)

      if (singleSelect) {
        /** Clicking the active option clears it — the only way back to "no filter". */
        onSelect(currentIndex === -1 ? [value] : [])

        return
      }

      if (currentIndex === -1) {
        onSelect([...selectedValues, value])
      } else {
        onSelect([
          ...selectedValues.slice(0, currentIndex),
          ...selectedValues.slice(currentIndex + 1),
        ])
      }
    },
    [onSelect, selectedValues, singleSelect],
  )

  const handleClear = useCallback(() => {
    onSelect([])
  }, [onSelect])

  const getItemChecked = useCallback(
    (value: V) => {
      return selectedValues.includes(value)
    },
    [selectedValues],
  )

  const handleEmpty = useCallback(() => {
    handleClose()

    if (onEmpty) {
      onEmpty()
    }
  }, [onEmpty])

  return (
    <Fragment>
      <ActionButton
        data-testid={triggerDataTestId}
        className={tw`px-4`}
        selected={!!anchorEl}
        color="white"
        text={title}
        icon={
          selectedValues?.length ? (
            <div css={tw`w-5 h-5 items-center justify-center flex rounded bg-[#7F00FF]`}>
              <Typography variant="overline" color="white">
                {selectedValues.length}
              </Typography>
            </div>
          ) : (
            icon
          )
        }
        onClick={handleClick}
      />
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        css={tw`mt-2 rounded-xl`}
      >
        {options.length ? (
          <div>
            <div css={tw`px-4 py-2 flex items-center justify-between`}>
              <Typography variant="h5">Select:</Typography>
              {selectedValues?.length ? (
                <div
                  data-testid={clearAllDataTestId}
                  css={tw`cursor-pointer hover:opacity-50`}
                  onClick={handleClear}
                >
                  <Typography variant="subtitle1" color={colors.PRIMARY_PURPLE_500}>
                    Clear all
                  </Typography>
                </div>
              ) : null}
            </div>
            {searchable && (
              <div css={tw`px-4 pb-2`}>
                <TextField
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={(event) => {
                    if (event.key.length === 1) {
                      event.stopPropagation()
                    }
                  }}
                  autoFocus
                  fullWidth
                  inputProps={{ 'data-testid': searchInputDataTestId }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchMinor width={16} height={16} />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            )}
            <List css={tw`max-h-[320px] overflow-y-auto`}>
              {filteredOptions.length ? (
                filteredOptions.map(({ value, component }) => {
                  const labelId = `checkbox-list-label-${name}`

                  return (
                    <ListItem css={{ width: menuWidth }} key={value} disablePadding>
                      <ListItemButton role={undefined} onClick={() => handleToggle(value)} dense>
                        <ListItemIcon css={tw`min-w-0 mr-2`}>
                          {singleSelect ? (
                            <Radio
                              edge="start"
                              checked={getItemChecked(value)}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{ 'aria-labelledby': labelId }}
                              sx={{ paddingRight: 0 }}
                            />
                          ) : (
                            <Checkbox
                              edge="start"
                              checked={getItemChecked(value)}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{ 'aria-labelledby': labelId }}
                              sx={{ paddingRight: 0 }}
                            />
                          )}
                        </ListItemIcon>
                        {typeof component === 'string' ? (
                          <Typography variant="subtitle1">{component}</Typography>
                        ) : (
                          component
                        )}
                      </ListItemButton>
                    </ListItem>
                  )
                })
              ) : (
                <Typography
                  variant="body2"
                  color={colors.SHADES_GREY_400}
                  css={tw`px-4 py-3 text-center block`}
                >
                  No results found
                </Typography>
              )}
            </List>
          </div>
        ) : (
          <div css={tw`px-6 py-4 w-[240px]`}>
            <Typography css={tw`text-center mb-0.5`} variant="h4">
              No options were created
            </Typography>
            <Typography css={tw`text-center`} variant="body2" color={colors.SHADES_GREY_400}>
              {emptyText}
            </Typography>
            {onEmpty && (
              <Typography
                data-testid={manageOptionsDataTestId}
                css={tw`text-center cursor-pointer mt-2`}
                color={colors.PRIMARY_PURPLE_500}
                variant="subtitle1"
                onClick={handleEmpty}
              >
                Manage options
              </Typography>
            )}
          </div>
        )}
      </Menu>
    </Fragment>
  )
}

export const TableSelect = memo(TableSelectComponent) as typeof TableSelectComponent
